import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Button from '@/Components/Button/Button';
import { Pill, Subtitle, Text, Title } from '@/Components/Preferences/PreferencesComponents/Content';
import HorizontalSeparator from '@/Components/Shared/HorizontalSeparator';
import { useApplication } from '@/Components/ApplicationProvider';
import EncryptionStatusItem from '../Security/EncryptionStatusItem';
import Icon from '@/Components/Icon/Icon';
import OfflineSubscription from '../General/Offline/OfflineSubscription';
import EnvironmentConfiguration from './Settings/EnvironmentConfiguration';
import DatabaseConfiguration from './Settings/DatabaseConfiguration';
import { classNames, sleep } from '@standardnotes/snjs';
import StatusIndicator from './Status/StatusIndicator';
import { PremiumFeatureIconClass, PremiumFeatureIconName } from '@/Components/Icon/PremiumFeatureIcon';
import Switch from '@/Components/Switch/Switch';
import AccordionItem from '@/Components/Shared/AccordionItem';
import PreferencesSegment from '../../PreferencesComponents/PreferencesSegment';
import PreferencesGroup from '../../PreferencesComponents/PreferencesGroup';
const HomeServerSettings = () => {
    const SERVER_SYNTHEIC_CHANGE_DELAY = 1500;
    const LOGS_REFRESH_INTERVAL = 5000;
    const application = useApplication();
    const homeServerService = application.homeServer;
    const featuresService = application.features;
    const sessionsService = application.sessions;
    const logsTextarea = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [showLogs, setShowLogs] = useState(false);
    const [logs, setLogs] = useState([]);
    const [status, setStatus] = useState();
    const [homeServerDataLocation, setHomeServerDataLocation] = useState('');
    const [isAPremiumUser, setIsAPremiumUser] = useState(false);
    const [isSignedIn, setIsSignedIn] = useState(false);
    const [showOfflineSubscriptionActivation, setShowOfflineSubscriptionActivation] = useState(false);
    const [logsIntervalRef, setLogsIntervalRef] = useState(null);
    const [homeServerConfiguration, setHomeServerConfiguration] = useState(null);
    const [homeServerEnabled, setHomeServerEnabled] = useState(false);
    const refreshStatus = useCallback(async () => {
        var _a;
        const result = await homeServerService.getHomeServerStatus();
        setStatus({
            state: result.status === 'on' ? 'online' : result.errorMessage ? 'error' : 'offline',
            message: result.status === 'on' ? 'Online' : result.errorMessage ? 'Offline' : 'Starting...',
            description: result.status === 'on' ? (_jsxs(_Fragment, { children: ["Accessible on local network at", ' ', _jsx("a", { href: result.url, className: "font-bold text-info", target: "_blank", children: result.url })] })) : ((_a = result.errorMessage) !== null && _a !== void 0 ? _a : 'Your home server is offline.'),
        });
    }, [homeServerService, setStatus]);
    const initialyLoadHomeServerConfiguration = useCallback(async () => {
        if (!homeServerConfiguration) {
            const homeServerConfiguration = await homeServerService.getHomeServerConfiguration();
            if (homeServerConfiguration) {
                setHomeServerConfiguration(homeServerConfiguration);
            }
        }
    }, [homeServerConfiguration, homeServerService]);
    const toggleHomeServer = useCallback(async () => {
        if ((status === null || status === void 0 ? void 0 : status.state) === 'restarting') {
            return;
        }
        if (homeServerEnabled) {
            setStatus({ state: 'restarting', message: 'Shutting down...' });
            const result = await homeServerService.disableHomeServer();
            await sleep(SERVER_SYNTHEIC_CHANGE_DELAY);
            if (result.isFailed() && (await homeServerService.isHomeServerRunning())) {
                setStatus({ state: 'error', message: result.getError() });
                return;
            }
            setHomeServerEnabled(await homeServerService.isHomeServerEnabled());
            await refreshStatus();
        }
        else {
            setStatus({ state: 'restarting', message: 'Starting...' });
            await homeServerService.enableHomeServer();
            setHomeServerEnabled(await homeServerService.isHomeServerEnabled());
            await sleep(SERVER_SYNTHEIC_CHANGE_DELAY);
            await refreshStatus();
            void initialyLoadHomeServerConfiguration();
        }
    }, [homeServerEnabled, homeServerService, status, refreshStatus, initialyLoadHomeServerConfiguration]);
    const clearLogs = useCallback((hideLogs = false) => {
        if (logsIntervalRef !== null) {
            clearInterval(logsIntervalRef);
        }
        if (hideLogs) {
            setShowLogs(false);
        }
        setLogs([]);
    }, [setLogs, logsIntervalRef]);
    const setupLogsRefresh = useCallback(async () => {
        clearLogs();
        setLogs(await homeServerService.getHomeServerLogs());
        const interval = setInterval(async () => {
            setLogs(await homeServerService.getHomeServerLogs());
        }, LOGS_REFRESH_INTERVAL);
        setLogsIntervalRef(interval);
    }, [homeServerService, clearLogs]);
    useEffect(() => {
        async function updateHomeServerDataLocation() {
            const location = await homeServerService.getHomeServerDataLocation();
            if (location) {
                setHomeServerDataLocation(location);
            }
        }
        void updateHomeServerDataLocation();
        async function updateHomeServerEnabled() {
            setHomeServerEnabled(await homeServerService.isHomeServerEnabled());
        }
        void updateHomeServerEnabled();
        setIsAPremiumUser(featuresService.hasOfflineRepo());
        setIsSignedIn(sessionsService.isSignedIn());
        void initialyLoadHomeServerConfiguration();
        void refreshStatus();
    }, [featuresService, sessionsService, homeServerService, refreshStatus, initialyLoadHomeServerConfiguration]);
    const handleHomeServerConfigurationChange = useCallback(async (changedServerConfiguration) => {
        try {
            setStatus({ state: 'restarting', message: 'Applying changes and restarting...' });
            setHomeServerConfiguration(changedServerConfiguration);
            await homeServerService.stopHomeServer();
            await sleep(SERVER_SYNTHEIC_CHANGE_DELAY);
            await homeServerService.setHomeServerConfiguration(changedServerConfiguration);
            clearLogs(true);
            const result = await homeServerService.startHomeServer();
            if (result !== undefined) {
                setStatus({ state: 'error', message: result });
            }
            void refreshStatus();
        }
        catch (error) {
            setStatus({ state: 'error', message: error.message });
        }
    }, [homeServerService, setStatus, setHomeServerConfiguration, refreshStatus, clearLogs]);
    const changeHomeServerDataLocation = useCallback(async (location) => {
        try {
            await homeServerService.stopHomeServer();
            if (location === undefined) {
                const oldLocation = await homeServerService.getHomeServerDataLocation();
                const newLocationOrError = await homeServerService.changeHomeServerDataLocation();
                if (newLocationOrError.isFailed()) {
                    setStatus({
                        state: 'error',
                        message: `${newLocationOrError.getError()}. Restoring to initial location in a moment...`,
                    });
                    await sleep(2 * SERVER_SYNTHEIC_CHANGE_DELAY);
                    await changeHomeServerDataLocation(oldLocation);
                    return;
                }
                location = newLocationOrError.getValue();
            }
            setStatus({ state: 'restarting', message: 'Applying changes and restarting...' });
            await sleep(SERVER_SYNTHEIC_CHANGE_DELAY);
            setHomeServerDataLocation(location);
            clearLogs(true);
            const result = await homeServerService.startHomeServer();
            if (result !== undefined) {
                setStatus({ state: 'error', message: result });
            }
            void refreshStatus();
        }
        catch (error) {
            setStatus({ state: 'error', message: error.message });
        }
    }, [homeServerService, setStatus, setHomeServerDataLocation, refreshStatus, clearLogs]);
    const openHomeServerDataLocation = useCallback(async () => {
        try {
            await homeServerService.openHomeServerDataLocation();
        }
        catch (error) {
            setStatus({ state: 'error', message: error.message });
        }
    }, [homeServerService]);
    const handleShowLogs = () => {
        const newValueForShowingLogs = !showLogs;
        if (newValueForShowingLogs) {
            void setupLogsRefresh();
        }
        else {
            if (logsIntervalRef) {
                clearInterval(logsIntervalRef);
                setLogsIntervalRef(null);
            }
        }
        setShowLogs(newValueForShowingLogs);
    };
    function isTextareaScrolledToBottom(textarea) {
        const { scrollHeight, scrollTop, clientHeight } = textarea;
        const scrolledToBottom = scrollTop + clientHeight >= scrollHeight;
        return scrolledToBottom;
    }
    useLayoutEffect(() => {
        if (logsTextarea.current) {
            setIsAtBottom(isTextareaScrolledToBottom(logsTextarea.current));
        }
    }, [logs]);
    useEffect(() => {
        const handleScroll = () => {
            if (logsTextarea.current) {
                setIsAtBottom(isTextareaScrolledToBottom(logsTextarea.current));
            }
        };
        const textArea = logsTextarea.current;
        if (textArea) {
            textArea.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (textArea) {
                textArea.removeEventListener('scroll', handleScroll);
            }
            if (logsIntervalRef !== null) {
                clearInterval(logsIntervalRef);
            }
        };
    }, [logsIntervalRef]);
    useEffect(() => {
        if (logsTextarea.current && isAtBottom) {
            logsTextarea.current.scrollTop = logsTextarea.current.scrollHeight;
        }
    }, [logs, isAtBottom]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex items-center justify-between", children: _jsxs("div", { className: "flex items-start", children: [_jsx(Title, { children: "Home Server" }), _jsx(Pill, { style: 'success', children: "Labs" })] }) }), _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "mr-10 flex flex-col", children: _jsx(Subtitle, { children: "Sync your data on a private cloud running on your home computer." }) }), _jsx(Switch, { disabled: (status === null || status === void 0 ? void 0 : status.state) === 'restarting', onChange: toggleHomeServer, checked: homeServerEnabled })] }), homeServerEnabled && (_jsxs("div", { children: [_jsx(StatusIndicator, { className: 'mr-3', status: status, homeServerService: homeServerService }), (status === null || status === void 0 ? void 0 : status.state) !== 'restarting' && (_jsxs(_Fragment, { children: [_jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs(_Fragment, { children: [_jsx(Text, { className: "mb-3", children: "Home server is enabled. All data is stored at:" }), _jsx(EncryptionStatusItem, { status: homeServerDataLocation || 'Not Set', icon: _jsx(Icon, { type: "attachment-file", className: "min-h-5 min-w-5" }), checkmark: false }), _jsxs("div", { className: "mt-2.5 flex flex-row", children: [_jsx(Button, { label: "Open Location", className: 'mr-3 text-xs', onClick: openHomeServerDataLocation }), _jsx(Button, { label: "Change Location", className: 'mr-3 text-xs', onClick: () => changeHomeServerDataLocation() })] })] }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsx(PreferencesGroup, { children: _jsx(PreferencesSegment, { children: _jsx(AccordionItem, { title: 'Logs', onClick: handleShowLogs, children: _jsx("div", { className: "flex flex-row items-center", children: _jsx("div", { className: "flex max-w-full flex-grow flex-col", children: _jsx("textarea", { ref: logsTextarea, disabled: true, className: "h-[500px] overflow-y-auto whitespace-pre-wrap bg-contrast p-2", value: logs.join('\n') }) }) }) }) }) }), homeServerConfiguration && (_jsxs(_Fragment, { children: [_jsx(HorizontalSeparator, { classes: "my-4" }), _jsx(DatabaseConfiguration, { homeServerConfiguration: homeServerConfiguration, setHomeServerConfigurationChangedCallback: handleHomeServerConfigurationChange }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsx(EnvironmentConfiguration, { homeServerConfiguration: homeServerConfiguration, setHomeServerConfigurationChangedCallback: handleHomeServerConfigurationChange })] })), isSignedIn && !isAPremiumUser && (_jsxs(_Fragment, { children: [_jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs("div", { className: 'mt-2 grid grid-cols-1 rounded-md border border-border p-4', children: [_jsxs("div", { className: "flex items-center", children: [_jsx(Icon, { className: classNames('-ml-1 mr-1 h-5 w-5', PremiumFeatureIconClass), type: PremiumFeatureIconName }), _jsx("h1", { className: "sk-h3 m-0 text-sm font-semibold", children: "Activate Premium Features" })] }), _jsx("p", { className: "col-start-1 col-end-3 m-0 mt-1 text-sm", children: "Enter your purchased offline subscription code to activate all the features offered by your home server, likes files support and Super notes." }), _jsx(Button, { primary: true, small: true, className: "col-start-1 col-end-3 mt-3 justify-self-start uppercase", onClick: () => {
                                                    setShowOfflineSubscriptionActivation(!showOfflineSubscriptionActivation);
                                                }, children: showOfflineSubscriptionActivation ? 'Close' : 'Activate Premium Features' }), showOfflineSubscriptionActivation && (_jsx(OfflineSubscription, { application: application, onSuccess: () => {
                                                    setIsAPremiumUser(true);
                                                    setShowOfflineSubscriptionActivation(false);
                                                } }))] })] }))] }))] }))] }));
};
export default HomeServerSettings;
